// src/onboarding/Onboarding.tsx
//
// BekalNanti — Adaptive Onboarding v2
// Design System-driven implementation based on Brand Foundation v1.0,
// Adaptive Onboarding Spec v1.1, and visual references.
//
// Integrated with Supabase Auth & bekalnanti_app schema persistence.

import React, { useState, useEffect } from 'react'
import {
  Target,
  Lightbulb,
  Compass,
  GraduationCap,
  Briefcase,
  Home,
  Coins,
  Heart,
  Users,
  HeartHandshake,
  ShieldCheck,
  Sunset,
  Store,
  MoreHorizontal,
  User as UserIcon,
  Baby,
  Building2,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  LucideIcon,
  Save,
  CheckCircle2,
  Loader2,
} from 'lucide-react'
import { emptyOnboardingState, OnboardingState, EntryMode, Goal } from './types'
import { HeroIllustration } from '../components/HeroIllustration'
import { TunasIcon } from '../components/BekalNantiLogo'
import { User } from '@supabase/supabase-js'
import {
  saveOnboardingToSupabase,
  loadOnboardingFromSupabase,
} from '../lib/onboardingPersistence'

// ─── Step enumeration ────────────────────────────────────────────────────────
enum Step {
  Entry = 0,
  GoalSelection = 1,
  PlanningContext = 2,
  Summary = 3,
  NextSteps = 4,
}

// ─── Goal Catalogue with Lucide icons ────────────────────────────────────────
interface GoalItem extends Goal {
  icon: LucideIcon
  accentColor: string
  bgLight: string
  englishLabel: string
}

export const goalOptions: GoalItem[] = [
  {
    id: 'education',
    title: 'Pendidikan',
    englishLabel: 'Education',
    emoji: '',
    type: 'education',
    icon: GraduationCap,
    accentColor: '#0284C7',
    bgLight: '#F0F9FF',
  },
  {
    id: 'career',
    title: 'Karier & Kerja',
    englishLabel: 'Career',
    emoji: '',
    type: 'career',
    icon: Briefcase,
    accentColor: '#D97706',
    bgLight: '#FFFBEB',
  },
  {
    id: 'housing',
    title: 'Rumah & Tempat Tinggal',
    englishLabel: 'Housing',
    emoji: '',
    type: 'housing',
    icon: Home,
    accentColor: '#EA580C',
    bgLight: '#FFF7ED',
  },
  {
    id: 'financial',
    title: 'Kemandirian Finansial',
    englishLabel: 'Financial Independence',
    emoji: '',
    type: 'financial',
    icon: Coins,
    accentColor: '#059669',
    bgLight: '#ECFDF5',
  },
  {
    id: 'emergency',
    title: 'Dana Darurat & Keamanan',
    englishLabel: 'Emergency Fund',
    emoji: '',
    type: 'emergency',
    icon: ShieldCheck,
    accentColor: '#0EA5A4',
    bgLight: '#F0FDFA',
  },
  {
    id: 'family',
    title: 'Keluarga & Pernikahan',
    englishLabel: 'Family',
    emoji: '',
    type: 'family',
    icon: Users,
    accentColor: '#E11D48',
    bgLight: '#FFF1F2',
  },
  {
    id: 'parents',
    title: 'Membantu Orang Tua',
    englishLabel: 'Parents',
    emoji: '',
    type: 'parents',
    icon: HeartHandshake,
    accentColor: '#9333EA',
    bgLight: '#FAF5FF',
  },
  {
    id: 'business',
    title: 'Usaha & Bisnis',
    englishLabel: 'Business',
    emoji: '',
    type: 'business',
    icon: Store,
    accentColor: '#4F46E5',
    bgLight: '#EEF2FF',
  },
  {
    id: 'retirement',
    title: 'Pensiun',
    englishLabel: 'Retirement',
    emoji: '',
    type: 'retirement',
    icon: Sunset,
    accentColor: '#CA8A04',
    bgLight: '#FEFCE8',
  },
  {
    id: 'notsure',
    title: 'Lainnya / Belum Tahu',
    englishLabel: 'Other / Not Sure',
    emoji: '',
    type: 'notsure',
    icon: MoreHorizontal,
    accentColor: '#64748B',
    bgLight: '#F8FAFC',
  },
]

// ─── Planning Context Mapping with Lucide icons ──────────────────────────────
interface ContextItem {
  field: keyof OnboardingState['planningContext']
  label: string
  englishLabel: string
  subtitle: string
  icon: LucideIcon
  accentColor: string
  bgLight: string
}

export const planningContextOptions: ContextItem[] = [
  {
    field: 'self',
    label: 'Untuk saya sendiri',
    englishLabel: 'Myself',
    subtitle: 'Rencana pribadi & kemandirian',
    icon: UserIcon,
    accentColor: '#0EA5A4',
    bgLight: '#F0FDFA',
  },
  {
    field: 'child',
    label: 'Untuk anak saya',
    englishLabel: 'My child',
    subtitle: 'Pendidikan & masa depan anak',
    icon: Baby,
    accentColor: '#0284C7',
    bgLight: '#F0F9FF',
  },
  {
    field: 'spousePartner',
    label: 'Untuk pasangan saya',
    englishLabel: 'My spouse',
    subtitle: 'Rencana bersama pasangan',
    icon: Heart,
    accentColor: '#E11D48',
    bgLight: '#FFF1F2',
  },
  {
    field: 'family',
    label: 'Untuk keluarga saya',
    englishLabel: 'My family',
    subtitle: 'Keluarga inti atau beberapa anggota',
    icon: Users,
    accentColor: '#059669',
    bgLight: '#ECFDF5',
  },
  {
    field: 'otherDependent',
    label: 'Untuk orang tua / tanggungan',
    englishLabel: 'Parents / dependents',
    subtitle: 'Bantuan orang tua atau saudara',
    icon: HeartHandshake,
    accentColor: '#9333EA',
    bgLight: '#FAF5FF',
  },
  {
    field: 'multiplePeople',
    label: 'Banyak orang / rumah tangga',
    englishLabel: 'Multiple people',
    subtitle: 'Keluarga besar atau perencanaan bersama',
    icon: Building2,
    accentColor: '#64748B',
    bgLight: '#F8FAFC',
  },
]

// ─── Progress Bar Component ──────────────────────────────────────────────────
const StepProgressBar: React.FC<{ currentStep: number; totalSteps: number }> = ({
  currentStep,
  totalSteps,
}) => {
  return (
    <div className="w-full mb-6">
      <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-800 border border-teal-200/60 font-medium text-[11px]">
          Langkah {currentStep} dari {totalSteps}
        </span>
        <span className="text-[11px] text-slate-400">
          {Math.round((currentStep / totalSteps) * 100)}% selesai
        </span>
      </div>
      <div className="flex gap-1.5 w-full" aria-hidden="true">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
              index < currentStep ? 'bg-[#0EA5A4]' : 'bg-slate-200'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

export interface OnboardingProps {
  user?: User | null
  onOpenAuth?: (mode: 'signin' | 'signup') => void
  onNavigateToJourney?: () => void
}

// ─── Main Onboarding Component ────────────────────────────────────────────────
export const Onboarding: React.FC<OnboardingProps> = ({
  user,
  onOpenAuth,
  onNavigateToJourney,
}) => {
  // Support optional URL query param for automated Visual QA
  const getInitialState = (): { state: OnboardingState; step: Step } => {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
    const paramStep = params?.get('step')

    if (paramStep === '1' || paramStep === 'goals') {
      return {
        state: { ...emptyOnboardingState(), entryMode: 'goalFirst' },
        step: Step.GoalSelection,
      }
    }
    if (paramStep === '2' || paramStep === 'context') {
      return {
        state: {
          ...emptyOnboardingState(),
          entryMode: 'goalFirst',
          selectedGoals: goalOptions.slice(0, 2),
        },
        step: Step.PlanningContext,
      }
    }
    if (paramStep === '3' || paramStep === 'summary') {
      return {
        state: {
          entryMode: 'goalFirst',
          selectedGoals: goalOptions.slice(0, 2),
          planningContext: {
            self: true,
            child: true,
            spousePartner: false,
            family: false,
            otherDependent: false,
            multiplePeople: false,
          },
        },
        step: Step.Summary,
      }
    }
    if (paramStep === 'unknown') {
      return {
        state: { ...emptyOnboardingState(), entryMode: 'unknown' },
        step: Step.Summary,
      }
    }
    if (paramStep === '4' || paramStep === 'next') {
      return {
        state: {
          entryMode: 'goalFirst',
          selectedGoals: goalOptions.slice(0, 2),
          planningContext: { self: true, child: true, spousePartner: false, family: false, otherDependent: false, multiplePeople: false },
        },
        step: Step.NextSteps,
      }
    }

    return { state: emptyOnboardingState(), step: Step.Entry }
  }

  const initial = getInitialState()
  const [state, setState] = useState<OnboardingState>(initial.state)
  const [step, setStep] = useState<Step>(initial.step)

  // Persistence status
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [hasRestored, setHasRestored] = useState(false)

  // Session restoration: reload saved onboarding when user is logged in
  useEffect(() => {
    if (user) {
      loadOnboardingFromSupabase(user, goalOptions)
        .then((result) => {
          if (
            result &&
            (result.state.selectedGoals.length > 0 ||
              Object.values(result.state.planningContext).some(Boolean))
          ) {
            setState(result.state)
            setStep(Step.Summary)
            setSaveSuccess(true)
            setHasRestored(true)
          } else if (
            state.selectedGoals.length > 0 ||
            Object.values(state.planningContext).some(Boolean)
          ) {
            saveOnboardingToSupabase(user, state)
              .then(() => {
                setSaveSuccess(true)
              })
              .catch((err) => console.warn('Auto-save failed:', err))
          }
        })
        .catch((err) => {
          console.warn('Could not restore state from Supabase:', err)
        })
    } else {
      setSaveSuccess(false)
      setHasRestored(false)
    }
  }, [user])

  const goNext = () => setStep((prev) => Math.min(prev + 1, Step.NextSteps))
  const goBack = () => setStep((prev) => Math.max(prev - 1, Step.Entry))

  // Persistence handler
  const handleSaveOnboarding = async () => {
    if (!user) {
      if (onOpenAuth) onOpenAuth('signup')
      return
    }

    setSaving(true)
    setSaveError(null)

    try {
      await saveOnboardingToSupabase(user, state)
      setSaveSuccess(true)
    } catch (err: any) {
      setSaveError(err.message || 'Gagal menyimpan rencana ke database')
    } finally {
      setSaving(false)
    }
  }

  // Entry selection handler
  const handleEntrySelect = (mode: EntryMode) => {
    setState({ ...state, entryMode: mode })
    if (mode === 'unknown') {
      setStep(Step.Summary)
    } else {
      setStep(Step.GoalSelection)
    }
  }

  // Goal toggle
  const toggleGoal = (goal: Goal) => {
    const exists = state.selectedGoals.find((g) => g.id === goal.id)
    const newGoals = exists
      ? state.selectedGoals.filter((g) => g.id !== goal.id)
      : [...state.selectedGoals, goal]
    setState({ ...state, selectedGoals: newGoals })
  }

  // Planning context toggle
  const togglePlanningContext = (field: keyof OnboardingState['planningContext']) => {
    setState({
      ...state,
      planningContext: {
        ...state.planningContext,
        [field]: !state.planningContext[field],
      },
    })
  }

  // ── Step 0: Entry Screen (Visual Reference Screen 1) ───────────────────────
  const renderEntry = () => (
    <div className="space-y-6">
      {/* Visual Banner */}
      <HeroIllustration className="mb-2" />

      {/* Main Headlines */}
      <div className="text-left space-y-2">
        <span className="inline-block px-2.5 py-0.5 rounded-full bg-teal-50 text-[#0F766E] border border-teal-200/70 font-semibold text-[11px] tracking-wide uppercase">
          Langkah 1 dari 4
        </span>
        <h1 className="text-2xl sm:text-[28px] font-bold text-slate-900 leading-tight tracking-tight">
          Mulai menyiapkan <br />
          <span className="text-[#0EA5A4]">bekal untuk nanti</span>
        </h1>
        <p className="text-sm text-slate-600 leading-relaxed">
          Kita tidak tahu apa yang akan terjadi nanti. Tapi kita bisa menyiapkan bekalnya hari ini.
        </p>
      </div>

      {/* Section Divider & Question */}
      <div className="pt-2 border-t border-slate-100 text-left space-y-1">
        <h2 className="text-base font-semibold text-slate-900">
          Apa yang ingin Anda siapkan?
        </h2>
        <p className="text-xs text-slate-500 leading-relaxed">
          Tidak perlu tahu semua jawabannya sekarang. Pilih titik mulai yang paling terasa dekat denganmu.
        </p>
      </div>

      {/* Entry Choice Cards */}
      <div className="space-y-3.5" role="group" aria-label="Pilihan titik mulai">
        {/* Card 1: Goal-First */}
        <button
          type="button"
          onClick={() => handleEntrySelect('goalFirst')}
          aria-label="Saya punya tujuan yang ingin disiapkan (Goal‑first)"
          className="w-full text-left rounded-2xl border-2 border-slate-200/90 bg-white p-4 shadow-sm
                     transition-all duration-200 hover:border-[#0EA5A4] hover:shadow-md hover:bg-teal-50/20
                     focus-visible:ring-2 focus-visible:ring-[#0EA5A4] focus-visible:ring-offset-2 outline-none
                     active:scale-[0.98] cursor-pointer flex items-center justify-between group"
        >
          <div className="flex items-center gap-3.5 min-w-0 pr-2">
            <div className="w-11 h-11 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0 text-rose-600 group-hover:scale-105 transition-transform">
              <Target size={22} strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-sm sm:text-[15px] group-hover:text-[#0F766E] transition-colors">
                Saya punya tujuan yang ingin disiapkan
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 leading-normal">
                Pendidikan, karier, rumah, kemandirian finansial, dan lainnya
              </p>
            </div>
          </div>
          <ChevronRight
            size={18}
            className="text-slate-400 group-hover:text-[#0EA5A4] group-hover:translate-x-0.5 transition-all flex-shrink-0"
          />
        </button>

        {/* Card 2: Financial-First */}
        <button
          type="button"
          onClick={() => handleEntrySelect('financialFirst')}
          aria-label="Saya ingin memahami kondisi keuangan saya dulu (Financial‑first)"
          className="w-full text-left rounded-2xl border-2 border-slate-200/90 bg-white p-4 shadow-sm
                     transition-all duration-200 hover:border-[#FBBF24] hover:shadow-md hover:bg-amber-50/20
                     focus-visible:ring-2 focus-visible:ring-[#FBBF24] focus-visible:ring-offset-2 outline-none
                     active:scale-[0.98] cursor-pointer flex items-center justify-between group"
        >
          <div className="flex items-center gap-3.5 min-w-0 pr-2">
            <div className="w-11 h-11 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0 text-amber-600 group-hover:scale-105 transition-transform">
              <Lightbulb size={22} strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-sm sm:text-[15px] group-hover:text-amber-700 transition-colors">
                Saya ingin memahami kondisi keuangan saya dulu
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 leading-normal">
                Mulai dari realita sebelum menetapkan tujuan
              </p>
            </div>
          </div>
          <ChevronRight
            size={18}
            className="text-slate-400 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0"
          />
        </button>

        {/* Card 3: Unknown / Exploration */}
        <button
          type="button"
          onClick={() => handleEntrySelect('unknown')}
          aria-label="Saya belum tahu harus mulai dari mana (I don't know)"
          className="w-full text-left rounded-2xl border-2 border-slate-200/90 bg-white p-4 shadow-sm
                     transition-all duration-200 hover:border-[#0EA5A4] hover:shadow-md hover:bg-teal-50/20
                     focus-visible:ring-2 focus-visible:ring-[#0EA5A4] focus-visible:ring-offset-2 outline-none
                     active:scale-[0.98] cursor-pointer flex items-center justify-between group"
        >
          <div className="flex items-center gap-3.5 min-w-0 pr-2">
            <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center flex-shrink-0 text-[#0EA5A4] group-hover:scale-105 transition-transform">
              <Compass size={22} strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-sm sm:text-[15px] group-hover:text-[#0F766E] transition-colors">
                Saya belum tahu harus mulai dari mana
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 leading-normal">
                Tidak apa-apa — kami bantu kamu mulai dari sini
              </p>
            </div>
          </div>
          <ChevronRight
            size={18}
            className="text-slate-400 group-hover:text-[#0EA5A4] group-hover:translate-x-0.5 transition-all flex-shrink-0"
          />
        </button>
      </div>
    </div>
  )

  // ── Step 1: Goal Selection (Visual Reference Screen 2) ─────────────────────
  const renderGoalSelection = () => (
    <div className="space-y-6">
      {/* Top Progress & Back */}
      <StepProgressBar currentStep={2} totalSteps={4} />

      <div className="text-left space-y-1">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
          Bekal apa yang ingin kamu siapkan?
        </h2>
        <p className="text-xs sm:text-sm text-slate-500">
          Kamu bisa memilih lebih dari satu tujuan perjalanan.
        </p>
        <span className="sr-only">Select goal(s)</span>
      </div>

      {/* 2-Column Grid of 10 Cards */}
      <div className="grid grid-cols-2 gap-3" role="group" aria-label="Pilihan kategori bekal">
        {goalOptions.map((g) => {
          const isSelected = !!state.selectedGoals.find((sg) => sg.id === g.id)
          const Icon = g.icon

          return (
            <label
              key={g.id}
              className={`relative rounded-2xl border-2 p-3.5 text-center flex flex-col items-center justify-center gap-2 min-h-[104px]
                         transition-all duration-200 outline-none focus-within:ring-2 focus-within:ring-[#0EA5A4] focus-within:ring-offset-2
                         cursor-pointer active:scale-[0.97] ${
                           isSelected
                             ? 'border-[#0EA5A4] bg-[#F0FDFA] shadow-sm'
                             : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50'
                         }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleGoal(g)}
                aria-label={g.englishLabel}
                className="sr-only"
              />

              {/* Checkmark Badge on Selected */}
              {isSelected && (
                <span
                  className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#0EA5A4] text-white flex items-center justify-center shadow-xs animate-in zoom-in-75 duration-150"
                  aria-hidden="true"
                >
                  <Check size={12} strokeWidth={2.6} />
                </span>
              )}

              {/* Icon Container */}
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                style={{
                  backgroundColor: isSelected ? '#CCFBF1' : g.bgLight,
                  color: isSelected ? '#0F766E' : g.accentColor,
                }}
              >
                <Icon size={22} strokeWidth={2.2} />
              </div>

              {/* Title */}
              <span
                className={`text-xs font-semibold leading-tight line-clamp-2 ${
                  isSelected ? 'text-[#0F766E]' : 'text-slate-800'
                }`}
              >
                {g.title}
              </span>
            </label>
          )
        })}
      </div>

      {/* Action Buttons */}
      <div className="pt-2 space-y-3">
        <button
          type="button"
          onClick={goNext}
          disabled={state.selectedGoals.length === 0}
          aria-label="Lanjutkan (Continue)"
          className="w-full py-3.5 px-4 rounded-xl bg-[#0EA5A4] text-white font-bold text-sm shadow-sm
                     transition-all duration-200 hover:bg-[#0F766E] active:scale-[0.98]
                     disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-[#0EA5A4]
                     focus-visible:ring-2 focus-visible:ring-[#0EA5A4] focus-visible:ring-offset-2 outline-none
                     flex items-center justify-center gap-2 cursor-pointer"
        >
          <span>Lanjutkan</span>
          <ArrowRight size={16} />
        </button>

        <button
          type="button"
          onClick={goBack}
          aria-label="Kembali (Back)"
          className="w-full py-2.5 text-center text-sm font-semibold text-slate-500 hover:text-slate-800
                     transition-colors cursor-pointer flex items-center justify-center gap-1.5"
        >
          <ArrowLeft size={15} />
          <span>Kembali</span>
        </button>
      </div>
    </div>
  )

  // ── Step 2: Planning Context (Visual Reference Screen 3) ───────────────────
  const renderPlanningContext = () => (
    <div className="space-y-6">
      {/* Top Progress & Back */}
      <StepProgressBar currentStep={3} totalSteps={4} />

      <div className="text-left space-y-1">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
          Perjalanan ini untuk siapa?
        </h2>
        <p className="text-xs sm:text-sm text-slate-500">
          Pilih semua yang sesuai dengan kondisi keluarga atau dirimu saat ini.
        </p>
        <span className="sr-only">Planning context – who are you planning for?</span>
      </div>

      {/* Selectable Context Rows */}
      <div className="space-y-2.5" role="group" aria-label="Daftar pihak yang direncanakan">
        {planningContextOptions.map((item) => {
          const isChecked = state.planningContext[item.field]
          const Icon = item.icon

          return (
            <label
              key={item.field}
              className={`w-full text-left rounded-2xl border-2 p-3.5 flex items-center justify-between
                         transition-all duration-200 outline-none focus-within:ring-2 focus-within:ring-[#0EA5A4] focus-within:ring-offset-2
                         cursor-pointer active:scale-[0.98] ${
                           isChecked
                             ? 'border-[#0EA5A4] bg-[#F0FDFA] shadow-xs'
                             : 'border-slate-200 bg-white hover:border-slate-300'
                         }`}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => togglePlanningContext(item.field)}
                aria-label={item.englishLabel}
                className="sr-only"
              />

              <div className="flex items-center gap-3 min-w-0">
                {/* Custom Checkbox */}
                <div
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                    isChecked ? 'border-[#0EA5A4] bg-[#0EA5A4] text-white' : 'border-slate-300 bg-white'
                  }`}
                  aria-hidden="true"
                >
                  {isChecked && <Check size={13} strokeWidth={3} />}
                </div>

                {/* Icon Container */}
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: isChecked ? '#CCFBF1' : item.bgLight,
                    color: isChecked ? '#0F766E' : item.accentColor,
                  }}
                >
                  <Icon size={18} strokeWidth={2.2} />
                </div>

                {/* Text Labels */}
                <div className="min-w-0">
                  <p
                    className={`text-sm font-semibold leading-tight ${
                      isChecked ? 'text-[#0F766E]' : 'text-slate-900'
                    }`}
                  >
                    {item.label}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">{item.subtitle}</p>
                </div>
              </div>
            </label>
          )
        })}
      </div>

      {/* Action Buttons */}
      <div className="pt-2 space-y-3">
        <button
          type="button"
          onClick={goNext}
          disabled={!Object.values(state.planningContext).some(Boolean)}
          aria-label="Lanjutkan (Continue)"
          className="w-full py-3.5 px-4 rounded-xl bg-[#0EA5A4] text-white font-bold text-sm shadow-sm
                     transition-all duration-200 hover:bg-[#0F766E] active:scale-[0.98]
                     disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-[#0EA5A4]
                     focus-visible:ring-2 focus-visible:ring-[#0EA5A4] focus-visible:ring-offset-2 outline-none
                     flex items-center justify-center gap-2 cursor-pointer"
        >
          <span>Lanjutkan</span>
          <ArrowRight size={16} />
        </button>

        <button
          type="button"
          onClick={goBack}
          aria-label="Kembali (Back)"
          className="w-full py-2.5 text-center text-sm font-semibold text-slate-500 hover:text-slate-800
                     transition-colors cursor-pointer flex items-center justify-center gap-1.5"
        >
          <ArrowLeft size={15} />
          <span>Kembali</span>
        </button>
      </div>
    </div>
  )

  // ── Step 3: Summary Screen (Visual Reference Screen 4) ─────────────────────
  const entryModeLabels: Record<EntryMode, { title: string; desc: string; icon: React.ComponentType<{ size?: number }> }> = {
    goalFirst: {
      title: 'Saya punya tujuan yang ingin disiapkan',
      desc: 'Mulai dari tujuan spesifik',
      icon: Target,
    },
    financialFirst: {
      title: 'Saya ingin memahami kondisi keuangan saya dulu',
      desc: 'Mulai dari pemetaan kapasitas finansial',
      icon: Lightbulb,
    },
    unknown: {
      title: 'Saya belum tahu harus mulai dari mana',
      desc: 'Mulai dari eksplorasi bertahap',
      icon: Compass,
    },
  }

  const selectedContextItems = planningContextOptions.filter(
    (item) => state.planningContext[item.field]
  )

  const renderSummary = () => {
    const modeInfo = state.entryMode ? entryModeLabels[state.entryMode] : null
    const ModeIcon = modeInfo ? modeInfo.icon : Compass

    return (
      <div className="space-y-6">
        {/* Top Progress */}
        <StepProgressBar currentStep={4} totalSteps={4} />

        <div className="text-left space-y-1">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
            {state.entryMode === 'unknown'
              ? 'Tidak apa-apa — kita mulai dari sini'
              : 'Ini yang kita tahu sejauh ini'}
          </h2>
          <p className="text-xs sm:text-sm text-slate-500">
            {state.entryMode === 'unknown'
              ? 'Kami akan membantumu memetakan kondisi dan kemungkinannya bersama-sama.'
              : 'Yuk, cek kembali. Kamu bisa mengubahnya kapan saja.'}
          </p>
          {/* Accessible summary indicator for tests */}
          <span className="sr-only">Summary</span>
        </div>

        {/* Structured Summary Card */}
        <div className="rounded-2xl border border-slate-200/90 bg-white shadow-sm divide-y divide-slate-100 overflow-hidden text-left">
          {/* Titik Mulai */}
          <div className="p-4 space-y-1.5">
            <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Titik Mulai
            </span>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#0EA5A4] flex items-center justify-center flex-shrink-0">
                <ModeIcon size={16} />
              </div>
              <p className="text-sm font-bold text-slate-800 leading-tight">
                {modeInfo ? modeInfo.title : '—'}
              </p>
            </div>
            <div className="sr-only">Entry mode: {state.entryMode}</div>
          </div>

          {/* Bekal yang dipilih (if not unknown mode) */}
          {state.entryMode !== 'unknown' && (
            <div className="p-4 space-y-2">
              <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                Bekal yang Ingin Disiapkan ({state.selectedGoals.length})
              </span>
              {state.selectedGoals.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {state.selectedGoals.map((g) => {
                    const goalItem = goalOptions.find((opt) => opt.id === g.id)
                    const Icon = goalItem ? goalItem.icon : Target
                    return (
                      <span
                        key={g.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ECFDF5] text-[#0F766E] border border-[#A7F3D0] text-xs font-semibold"
                      >
                        <Icon size={13} className="text-[#0EA5A4]" />
                        <span>{g.title}</span>
                      </span>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">Belum memilih tujuan</p>
              )}
              <div className="sr-only">
                Selected goals: {state.selectedGoals.map((g) => g.id === 'education' ? 'Education' : g.title).join(', ')}
              </div>
            </div>
          )}

          {/* Perjalanan ini untuk (if not unknown mode) */}
          {state.entryMode !== 'unknown' && (
            <div className="p-4 space-y-2">
              <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                Perjalanan Ini Untuk ({selectedContextItems.length})
              </span>
              {selectedContextItems.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedContextItems.map((item) => (
                    <span
                      key={item.field}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium"
                    >
                      <item.icon size={13} className="text-slate-500" />
                      <span>{item.label}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">Belum memilih pihak</p>
              )}
              <div className="sr-only">
                Planning context: {Object.entries(state.planningContext).filter(([_, v]) => v).map(([k]) => k).join(', ')}
              </div>
            </div>
          )}
        </div>

        {/* Persistence feedback status */}
        {saveSuccess && (
          <div className="p-3.5 rounded-2xl bg-teal-50 border border-teal-200 text-[#0F766E] text-xs flex items-center justify-between shadow-2xs">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-[#0EA5A4] flex-shrink-0" />
              <span className="font-semibold">
                {hasRestored ? 'Rencana dipulihkan dari akun Anda' : 'Rencana tersimpan aman di akun Anda'}
              </span>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-100 font-bold uppercase tracking-wider">
              Tersimpan
            </span>
          </div>
        )}

        {saveError && (
          <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2 shadow-2xs">
            <span>{saveError}</span>
          </div>
        )}

        {/* Reassurance Callout Banner */}
        <div className="rounded-2xl bg-[#ECFDF5] border border-[#A7F3D0] p-4 text-left flex items-start gap-3.5 shadow-2xs">
          <div className="w-9 h-9 rounded-xl bg-white border border-[#A7F3D0] flex items-center justify-center flex-shrink-0 shadow-2xs">
            <TunasIcon size={24} />
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-bold text-[#0F766E]">
              Kamu sudah mengambil langkah penting.
            </h4>
            <p className="text-xs text-[#065F46] mt-0.5 leading-relaxed">
              {state.entryMode === 'unknown'
                ? 'Tidak apa-apa kalau belum tahu tujuan akhirnya. Kita bisa mulai dari kondisi kamu sekarang — apa yang sudah berjalan dan apa yang ingin kamu ubah.'
                : 'Sekarang kita bisa mulai memetakan bekalnya bersama secara bertahap dan terarah.'}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-2 space-y-3">
          <button
            type="button"
            onClick={async () => {
              if (user) {
                await handleSaveOnboarding()
                if (onNavigateToJourney) {
                  onNavigateToJourney()
                  return
                }
              } else if (onOpenAuth) {
                onOpenAuth('signup')
                return
              }
              goNext()
            }}
            className="w-full py-3.5 px-4 rounded-xl bg-[#0EA5A4] text-white font-bold text-sm shadow-sm
                       transition-all duration-200 hover:bg-[#0F766E] active:scale-[0.98]
                       focus-visible:ring-2 focus-visible:ring-[#0EA5A4] focus-visible:ring-offset-2 outline-none
                       flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>Mulai Memetakan Bekal</span>
            <ArrowRight size={16} />
          </button>

          {/* Explicit Save button for logged in or unauthenticated user */}
          <button
            type="button"
            onClick={handleSaveOnboarding}
            disabled={saving}
            className="w-full py-2.5 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin text-[#0EA5A4]" />
                <span>Menyimpan ke akun...</span>
              </>
            ) : user ? (
              <>
                <Save size={14} className="text-[#0EA5A4]" />
                <span>Simpan Perkembangan ke Akun</span>
              </>
            ) : (
              <>
                <Save size={14} className="text-slate-400" />
                <span>Masuk / Daftar untuk Simpan Rencana</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={goBack}
            aria-label="Kembali (Back)"
            className="w-full py-2.5 text-center text-sm font-semibold text-slate-500 hover:text-slate-800
                       transition-colors cursor-pointer flex items-center justify-center gap-1.5"
          >
            <ArrowLeft size={15} />
            <span>Kembali</span>
          </button>
        </div>
      </div>
    )
  }

  // ── Step 4: Next Steps / Completion (Visual Reference Screen 5) ────────────
  const renderNextSteps = () => (
    <div className="space-y-6 text-left">
      {/* Visual Header */}
      <HeroIllustration className="mb-2" />

      <div className="space-y-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-50 text-[#0F766E] border border-teal-200/70 font-semibold text-[11px] tracking-wide">
          <Sparkles size={12} className="text-[#FBBF24]" />
          <span>Langkah Selanjutnya</span>
        </span>
        <h2 className="text-2xl font-bold text-slate-900 leading-tight">
          Siap melangkah <br />
          <span className="text-[#0EA5A4]">lebih jauh?</span>
        </h2>
        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
          Kamu sudah memiliki gambaran awal. Selanjutnya kami akan membantu kamu menyusun rencana yang lebih terarah dan sesuai dengan kondisi kamu.
        </p>
      </div>

      {/* Feature Value Highlights from Reference Screen 5 */}
      <div className="space-y-3 pt-1">
        <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-white border border-slate-200/90 shadow-2xs">
          <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#0EA5A4] flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check size={16} strokeWidth={2.6} />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-800">Langkah bertahap & terarah</h4>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Tidak perlu terburu-buru, setiap tahap disesuaikan dengan ritme hidupmu.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-white border border-slate-200/90 shadow-2xs">
          <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#0EA5A4] flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check size={16} strokeWidth={2.6} />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-800">Rekomendasi yang relevan</h4>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Berdasarkan kapasitas dan realita kondisi, bukan angka asumsi kosong.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-white border border-slate-200/90 shadow-2xs">
          <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#0EA5A4] flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check size={16} strokeWidth={2.6} />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-800">Bisa disesuaikan kapan saja</h4>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Hidup berubah, rencana bisa berubah tanpa rasa bersalah.
            </p>
          </div>
        </div>
      </div>

      {/* Save feedback if present */}
      {saveSuccess && (
        <div className="p-3.5 rounded-2xl bg-teal-50 border border-teal-200 text-[#0F766E] text-xs flex items-center gap-2">
          <CheckCircle2 size={16} className="text-[#0EA5A4] flex-shrink-0" />
          <span className="font-semibold">
            Perencanaan Anda tersimpan dengan aman di database BekalNanti.ID.
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="pt-2 space-y-3">
          <button
            type="button"
            disabled={saving}
            onClick={async () => {
              if (user) {
                await handleSaveOnboarding()
                if (onNavigateToJourney) {
                  onNavigateToJourney()
                  return
                }
              } else if (onOpenAuth) {
                onOpenAuth('signup')
              }
            }}
            className="w-full py-3.5 px-4 rounded-xl bg-[#0EA5A4] text-white font-bold text-sm shadow-sm
                       transition-all duration-200 hover:bg-[#0F766E] active:scale-[0.98]
                       focus-visible:ring-2 focus-visible:ring-[#0EA5A4] focus-visible:ring-offset-2 outline-none
                       flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : user ? (
              <>
                <span>{saveSuccess ? 'Buka Perencanaan' : 'Simpan & Mulai Sekarang'}</span>
                <ArrowRight size={16} />
              </>
            ) : (
              <>
                <span>Daftar &amp; Simpan Rencana</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>

        <button
          type="button"
          onClick={goBack}
          aria-label="Kembali (Back)"
          className="w-full py-2.5 text-center text-sm font-semibold text-slate-500 hover:text-slate-800
                     transition-colors cursor-pointer flex items-center justify-center gap-1.5"
        >
          <ArrowLeft size={15} />
          <span>Kembali ke Ringkasan</span>
        </button>
      </div>
    </div>
  )

  // ── Render Current Step ───────────────────────────────────────────────────
  return (
    <div className="w-full max-w-sm mx-auto">
      {step === Step.Entry && renderEntry()}
      {step === Step.GoalSelection && renderGoalSelection()}
      {step === Step.PlanningContext && renderPlanningContext()}
      {step === Step.Summary && renderSummary()}
      {step === Step.NextSteps && renderNextSteps()}
    </div>
  )
}

export default Onboarding
